import { Button, Callout, Card, Heading, Paragraph, SyntaxIcon, Text, useDialog } from '@expressable/syntax-ds';
import { SealQuestion } from '@phosphor-icons/react';
import { useCognitoUserContext } from 'domains/auth/AWSCognitoWrapper/context';
import { SignupLoginModal } from 'domains/auth/SignupLoginModal/SignupLoginModal';
import { useContactById } from 'domains/contact/hooks';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseContactInfoSchema } from '../schemas/common';
import { SignupError } from 'utils/errors/debuggableError';
import { useTrack } from 'utils/hooks/useMixPanel';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from '../misc/mixpanelTracking';
import { useEffectOnce } from 'react-use';

type Props = {
  onLoginPrompt?: () => void;
  onLogoutPrompt?: () => void;
};

export const SignupLoginChecker = ({ onLoginPrompt, onLogoutPrompt }: Props) => {
  const cognito = useCognitoUserContext();

  const { data: contact } = useContactById({ contactId: cognito.isLoggedIn ? cognito.user.username : undefined });

  const { dialogStateProps, onOpen } = useDialog();

  const logoutHandler = useCallback(() => {
    onLogoutPrompt?.();
    if (cognito.isLoggedIn) {
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CONTACT_CHECKER.TRIGGERED_LOGOUT);
      cognito.signOut();
    }
  }, [cognito, onLogoutPrompt]);

  const openLoginHandler = useCallback(() => {
    onLoginPrompt?.();
    onOpen();
    track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CONTACT_CHECKER.TRIGGERED_LOGIN.TRIGGERED_MODAL);
  }, [onLoginPrompt, onOpen]);

  const { t } = useTranslation();

  const { track } = useTrack();

  useEffectOnce(() => {
    if (cognito.isLoggedIn) {
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CONTACT_CHECKER.LOGGED_IN);
    } else {
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CONTACT_CHECKER.LOGGED_OUT);
    }
  });

  /** Check for bad data on contact, force logout and throw error
   * More graceful handling of this will be added in the future
   */
  useEffect(() => {
    if (cognito.isLoggedIn && contact) {
      const parseResult = BaseContactInfoSchema.required().safeParse({
        firstName: contact?.contactFirstName,
        lastName: contact?.contactLastName,
        email: contact?.email,
        phone: contact?.phone?.mobile ?? '',
        contactID: contact?.contactID,
      });

      if (!parseResult.success) {
        track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CONTACT_CHECKER.TRIGGERED_LOGIN.FAILURE);
        logoutHandler();
        throw new SignupError('Contact data is invalid, not passing validation. Forcing log out', {
          debugData: {
            parseResult,
            contact,
          },
        });
      }
    }
  }, [cognito, contact]);

  const LoggedOutVariant = (
    <>
      <Card className="w-full gap-e16 flex flex-col" data-testid="signUp-loginChecker-logged-out">
        <Heading level="h6" className="flex items-center gap-e16">
          <div className="flex items-center justify-center rounded-full size-e40 bg-orchid-50">
            <SyntaxIcon icon={SealQuestion} weight="fill" className="text-purple-500" size="lg" />
          </div>
          {t('directSignup.contactChecker.loggedOut.title')}
        </Heading>
        <Paragraph>
          {t('directSignup.contactChecker.loggedOut.subtitle')}{' '}
          <Button variant="link" size="md" onClick={openLoginHandler} data-testid="signUp-login">
            {t('directSignup.contactChecker.loggedOut.clickHereToLogIn')}
          </Button>
        </Paragraph>
      </Card>

      <SignupLoginModal {...dialogStateProps} />
    </>
  );

  const LoggedInVariant = (
    <Callout
      type={cognito.isLoggedIn ? 'info' : 'default'}
      className="w-full"
      data-testid="signUp-loginChecker-logged-in"
    >
      <Text variant="body-md" data-testid="signUp-loginChecker-logged-in-title">
        {t('directSignup.contactChecker.loggedIn.title')}

        <span className="font-bold" data-testid="signUp-loginChecker-contact-name">
          {contact?.contactFirstName} {contact?.contactLastName}
        </span>
      </Text>
      <Text variant="body-sm">
        {t('directSignup.contactChecker.loggedIn.notYou')}{' '}
        <Button variant="link" size="md" onClick={logoutHandler} data-testid="signUp-logout">
          {t('directSignup.contactChecker.loggedIn.signOut')}
        </Button>
      </Text>
    </Callout>
  );

  if (cognito.isLoggedIn) {
    return LoggedInVariant;
  }

  return LoggedOutVariant;
};
